import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { isPreviewing } from '@builder.io/sdk-react';
import { domPurifyWithConfigs } from '@wonderful/wwc/dist/src/utils/domPurifyWithConfigs';

import { BuilderEditingHelper } from '@wonderful/wwc/dist/src/common/BuilderEditingHelper/BuilderEditingHelper';
import { clsx } from '@wonderful/wwc/dist/src/helpers/clsx';
import { DialogOverlay } from '@wonderful/wwc/dist/src/common/Dialog/DialogOverlay';
import { DialogContent } from '@wonderful/wwc/dist/src/common/Dialog/DialogContent';

import { LocaleEnum } from '@/helpers/enums';
import { ProductMainProps } from './ProductMain.types';
import { ColorTextEnum } from '@/store/types';
import { useTranslation } from '@/app/i18n/TranslationContext';
import { useIsAlmondPage } from '@/helpers/utils';
import { getBuilderPreviewProductsData } from '@/helpers/getBuilderProductData';

import BuilderImage from '@/components/common/BuilderImage/BuilderImage';
import {
  IconHeartHealthy,
  IconNonGmo,
  IconPlantProtein,
  IconModal,
} from './ProductMainIcons';
import Section from '@/components/design/Section/Section';
import NutritionLabel from '@/components/modules/NutritionLabel/NutritionLabel';
import Button from '@/components/common/Button/Button';
import styles from './ProductMain.module.scss';

export default function ProductMain(props: ProductMainProps) {
  const { locale, t } = useTranslation();

  const [products, setProducts] = useState([
    { product: props.productDetails },
    { product: props.productDetailsLeft },
    { product: props.productDetailsRight },
  ]);

  const [isNutritionLabelOpened, setIsNutritionLabelOpened] = useState(false);

  const colorData = products[0].product?.value?.data
    .color as unknown as keyof typeof ColorTextEnum;
  const color = ColorTextEnum[colorData];

  const isAlmondPage = useIsAlmondPage();

  useEffect(() => {
    async function setPreviewData() {
      const mergedProducts = await getBuilderPreviewProductsData(
        [
          { product: props.productDetails },
          { product: props.productDetailsLeft },
          { product: props.productDetailsRight },
        ],
        locale as LocaleEnum
      );
      mergedProducts && setProducts(mergedProducts);
    }

    isPreviewing() && setPreviewData();
  }, [
    props.productDetails,
    props.productDetailsLeft,
    props.productDetailsRight,
    locale,
  ]);

  useEffect(() => {
    // handle PDP theme colors
    if (color) {
      document.documentElement.style.setProperty(
        '--primary-theme-color',
        color
      );
      return () => {
        document.documentElement.style.setProperty(
          '--primary-theme-color',
          ColorTextEnum['none']
        );
      };
    }
  }, [color]);

  // handle transitioning between product pages
  const [isLoaded, setIsLoaded] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    setIsLoaded(true);
  }, [pathname, searchParams]);

  if (!products[2]?.product?.value?.data)
    return <BuilderEditingHelper componentName="productMain" visible={true} />;

  const {
    name,
    description,
    image,
    imageAltText,
    heartHealthy,
    nonGmo,
    plantProtein,
    nutritionLabel,
    disclosure,
  } = products[0]?.product?.value.data;

  const productDetailCategory =
    products[0].product.value.data.productDetailCategory !== 'None'
      ? products[0].product.value.data.productDetailCategory
      : null;

  if (!products[2]?.product?.value?.data) return null;

  const prevProduct = products[1].product.value.data.productDetailUrl;
  const nextProduct = products[2].product.value.data.productDetailUrl;

  return (
    <Section
      data-testid={'product-details'}
      className={[
        'container',
        styles.productContainer,
        isLoaded ? styles.animateIn : styles.animateOut,
      ].join(' ')}
    >
      {nutritionLabel ? (
        <DialogOverlay
          isOpen={isNutritionLabelOpened}
          onDismiss={() => setIsNutritionLabelOpened(false)}
        >
          <DialogContent
            aria-labelledby="nutrition-label-heading"
            ariaLabel={`${name} - Nutrition label`}
            isOpen={isNutritionLabelOpened}
            className={styles.nutritionLabel}
          >
            <NutritionLabel
              isOpen={isNutritionLabelOpened}
              label={nutritionLabel}
              onClose={() => setIsNutritionLabelOpened(false)}
              dataTestid={'nutrition-label-popup'}
            />
          </DialogContent>
        </DialogOverlay>
      ) : null}
      <div className="row">
        {prevProduct && (
          <Link
            href={prevProduct}
            className={locale === 'ar' ? styles.rightArrow : styles.leftArrow}
            aria-label="Previous product"
            data-testid={'prev-product'}
          >
            <span>{/* CSS arrows */}</span>
          </Link>
        )}
        {/* Product Heading - Mobile Only */}
        <div
          className={clsx(
            'col-12',
            'col-lg-6',
            'd-flex',
            'd-lg-none',
            styles.productHeadingBlock
          )}
        >
          <h1 className={styles.productTitle}>{name}</h1>
          <span className={styles.productCategoryContainer}>
            Wonderful<sup>®</sup>&lrm;
            {isAlmondPage ? 'Almonds: ' : 'Pistachios: '}
            {productDetailCategory && (
              <Link
                href={`/${locale}/products`}
                className={styles.productCategory}
              >
                <span aria-hidden={true}>{t(productDetailCategory)}</span>
              </Link>
            )}
          </span>
        </div>

        {/* Product Image */}
        <div
          className={['col-12 col-lg-6', styles.productImageContainer].join(
            ' '
          )}
        >
          {image && (
            <div className={styles.productImage}>
              <BuilderImage
                data-testid={'product-details-image'}
                imageSrc={image}
                mobileWidth={'200px'}
                tabletWidth={'300px'}
                alt={imageAltText || ''}
                disableLazyLoad={true}
              />
            </div>
          )}
        </div>

        {/* Product Details */}
        <div
          className={['col-12 col-lg-6', styles.productDetailsContainer].join(
            ' '
          )}
        >
          {/* Product Heading - Tablet/Desktop */}
          <div
            className={['d-none d-lg-flex', styles.productHeadingBlock].join(
              ' '
            )}
          >
            <h1>{name}</h1>
            <span className={styles.productCategoryContainer}>
              Wonderful<sup>®</sup>&lrm;
              {isAlmondPage ? ' Almonds: ' : ' Pistachios: '}
              {productDetailCategory && (
                <Link
                  href={`/${locale}/products`}
                  className={styles.productCategory}
                >
                  <span aria-hidden={true}>
                    {productDetailCategory && t(productDetailCategory)}
                  </span>
                </Link>
              )}
            </span>
          </div>

          {description && (
            <div
              className={styles.productDescription}
              data-testid={'product-details-description'}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: domPurifyWithConfigs(description),
                }}
              />
            </div>
          )}

          <div className={styles.buttonContainer}>
            <Button
              data-testid={'product-details-button'}
              as="Link"
              href={`/${locale}/where-to-buy`}
              design="round"
              className={styles.button}
            >
              {t('where to buy')}
            </Button>
          </div>
          {nutritionLabel && (
            <a
              className={styles.nutritionalFactsLink}
              tabIndex={0}
              onClick={() => setIsNutritionLabelOpened(true)}
              data-testid={'nutrition-toggle'}
            >
              {t('Nutrition Facts')}
              <IconModal />
            </a>
          )}

          {disclosure && (
            <ul className={styles.disclosures}>
              <li className={styles.disclosure} tabIndex={0}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: domPurifyWithConfigs(disclosure),
                  }}
                />
              </li>
            </ul>
          )}

          <div className={styles.iconsContainer}>
            {heartHealthy && <IconHeartHealthy />}
            {nonGmo && <IconNonGmo />}
            {plantProtein && <IconPlantProtein />}
          </div>
        </div>
        {nextProduct && (
          <Link
            href={nextProduct}
            className={locale === 'ar' ? styles.leftArrow : styles.rightArrow}
            aria-label="Next product"
            data-testid={'next-product'}
          >
            <span>{/* CSS arrows */}</span>
          </Link>
        )}
      </div>
    </Section>
  );
}
